import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Check, Upload, FileText, X, Loader2,
  CheckCircle2, Copy, ShieldCheck, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { rfqOptions, IMG } from "../lib/data";
import { uploadFile, submitRFQ } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import UrgentBanner from "../components/UrgentBanner";

const INITIAL = {
  need: "", tower_type: "", tower_height: "", tower_quantity: "", tower_config: "",
  tower_config_description: "", standards: [], scope_of_work: [],
  infra_type: [], infra_scale: "", infra_scope: [],
  building_type: "", building_area: "", building_floors: "", building_scope: [],
  material_category: [], material_quantity: "", material_purpose: "",
  eng_service: [], eng_project_type: "", eng_deliverable: [],
  project_status: "", delivery_time: "", priority: "",
  has_documents: "", document_types: [], files: [],
  country: "", city: "", project_name: "", site_name: "", gps_coordinates: "", maps_link: "",
  full_name: "", company_name: "", position: "", email: "", mobile: "", whatsapp: "", notes: "",
  project_description: "",
};

const isValidPhone = (v) => /^\+?\d{8,15}$/.test((v || "").trim());
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());
const sanitizePhone = (v) => (v || "").replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "").slice(0, 16);

const SelectCard = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`relative text-start px-5 py-4 border text-sm font-medium transition-all duration-200 ${
      active
        ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A1223]"
        : "bg-[#0A1223] border-white/12 text-slate-200 hover:border-[#D4AF37]/60"
    }`}
  >
    {children}
    {active && <Check size={16} className="absolute top-3 end-3" />}
  </button>
);

const TextInput = ({ label, optional, required, ...props }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-300 mb-1.5 block">
      {label}
      {required && <span className="text-[#D4AF37] ms-1">*</span>}
      {optional && <span className="text-slate-400 text-xs"> ({optional})</span>}
    </span>
    <input
      {...props}
      className="w-full bg-[#0A1223] border border-white/12 text-white px-4 py-3 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-colors placeholder:text-slate-600"
    />
  </label>
);

const StepTitle = ({ children, hint }) => (
  <div className="mb-6">
    <h2 className="font-heading text-2xl font-bold text-white">{children}</h2>
    {hint && <p className="text-sm text-slate-400 mt-1">{hint}</p>}
  </div>
);

const RequestQuotation = () => {
  const { t, lang, isRTL } = useLanguage();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [data, setData] = useState(INITIAL);

  const upd = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) =>
    setData((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  // Dynamic step flow
  const categorySteps = {
    "Telecom Towers": ["towerType", "towerHeight", "towerQuantity", "towerConfig", "standards", "scope", "description"],
    "Telecom Infrastructure": ["infraType", "infraScale", "infraScope", "standards", "description"],
    "Buildings & Civil Projects": ["buildingType", "buildingArea", "buildingFloors", "buildingScope", "standards", "description"],
    "Telecom Materials & Equipment": ["materialCategory", "materialQuantity", "materialPurpose", "description"],
    "Engineering Services": ["engService", "engProjectType", "engDeliverable", "description"],
    "Other": ["description"],
  };
  const steps = [
    "need",
    ...(categorySteps[data.need] || []),
    "status", "delivery", "priority", "documents", "location", "customer",
  ];
  const TOTAL = steps.length;
  const current = steps[Math.min(stepIdx, TOTAL - 1)];

  const labelMap = {
    need: t.rfqPage.sNeed, towerType: t.rfqPage.sTowerType, towerHeight: t.rfqPage.sTowerHeight,
    towerQuantity: t.rfqPage.sTowerQuantity, towerConfig: t.rfqPage.sTowerConfig,
    standards: t.rfqPage.sStandards, scope: t.rfqPage.sScope,
    infraType: t.rfqPage.sInfraType, infraScale: t.rfqPage.sInfraScale, infraScope: t.rfqPage.sScope,
    buildingType: t.rfqPage.sBuildingType, buildingArea: t.rfqPage.sBuildingArea,
    buildingFloors: t.rfqPage.sBuildingFloors, buildingScope: t.rfqPage.sScope,
    materialCategory: t.rfqPage.sMaterialCategory, materialQuantity: t.rfqPage.sMaterialQuantity,
    materialPurpose: t.rfqPage.sMaterialPurpose,
    engService: t.rfqPage.sEngService, engProjectType: t.rfqPage.sEngProjectType,
    engDeliverable: t.rfqPage.sEngDeliverable,
    status: t.rfqPage.sStatus, delivery: t.rfqPage.sDelivery, priority: t.rfqPage.sPriority,
    documents: t.rfqPage.sDocuments, location: t.rfqPage.sLocation, customer: t.rfqPage.sCustomer,
    description: t.rfqPage.sDescription,
  };

  const descLabelMap = {
    "Telecom Towers": t.rfqPage.descGeneral,
    "Telecom Infrastructure": t.rfqPage.descGeneral,
    "Buildings & Civil Projects": t.rfqPage.descGeneral,
    "Telecom Materials & Equipment": t.rfqPage.descMaterials,
    "Engineering Services": t.rfqPage.descEng,
    "Other": t.rfqPage.descOther,
  };
  const descLabel = descLabelMap[data.need] || t.rfqPage.descGeneral;

  const emailErr = !!data.email && !isValidEmail(data.email);
  const mobileErr = !!data.mobile && !isValidPhone(data.mobile);
  const whatsappErr = !!data.whatsapp && !isValidPhone(data.whatsapp);

  const canNext = () => {
    if (current === "need") return !!data.need;
    if (current === "towerType") return !!data.tower_type;
    if (current === "towerHeight") return !!data.tower_height;
    if (current === "towerQuantity") return !!data.tower_quantity;
    if (current === "towerConfig") return !!data.tower_config;
    if (current === "infraType") return data.infra_type.length > 0;
    if (current === "infraScale") return !!data.infra_scale;
    if (current === "buildingType") return !!data.building_type;
    if (current === "buildingArea") return !!data.building_area;
    if (current === "buildingFloors") return !!data.building_floors;
    if (current === "materialCategory") return data.material_category.length > 0;
    if (current === "materialQuantity") return !!data.material_quantity;
    if (current === "materialPurpose") return !!data.material_purpose;
    if (current === "engService") return data.eng_service.length > 0;
    if (current === "engProjectType") return !!data.eng_project_type;
    if (current === "description") return !!(data.project_description && data.project_description.trim());
    if (current === "location") return data.country && data.city && data.project_name;
    if (current === "customer")
      return data.full_name && data.company_name && isValidEmail(data.email) && isValidPhone(data.mobile) && isValidPhone(data.whatsapp);
    return true;
  };

  const next = () => {
    if (!canNext()) {
      const msg =
        current === "customer" || current === "location"
          ? "Please complete all required fields."
          : current === "description"
          ? t.rfqPage.descHint
          : t.rfqPage.selectRequired;
      toast.error(msg);
      return;
    }
    setStepIdx((s) => Math.min(s + 1, TOTAL - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStepIdx((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onFiles = async (fileList) => {
    const arr = Array.from(fileList);
    if (!arr.length) return;
    setUploading(true);
    try {
      for (const f of arr) {
        const res = await uploadFile(f);
        setData((p) => ({
          ...p,
          files: [...p.files, {
            id: res.id, original_filename: res.original_filename, storage_path: res.storage_path,
            content_type: res.content_type, size: res.size,
          }],
        }));
      }
      toast.success("Files uploaded");
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };
  const removeFile = (id) => upd("files", data.files.filter((f) => f.id !== id));

  const submit = async () => {
    if (!data.full_name || !data.company_name) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (!isValidEmail(data.email)) { toast.error(t.rfqPage.invalidEmail); return; }
    if (!isValidPhone(data.mobile)) { toast.error(t.rfqPage.invalidPhone); return; }
    if (!isValidPhone(data.whatsapp)) { toast.error(t.rfqPage.invalidPhone); return; }
    setSubmitting(true);
    try {
      const res = await submitRFQ({ ...data, inquiry_type: data.need });
      trackEvent("generate_lead", {
        inquiry_type: data.need,
        country: data.country,
        reference: res.reference,
      });
      setSuccess(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const SingleGrid = ({ field, options, cols }) => (
    <div className={`grid ${cols || "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"} gap-3`}>
      {options.map((o) => (
        <SelectCard key={o.v} active={data[field] === o.v} onClick={() => upd(field, o.v)} testid={`opt-${field}-${o.v}`}>
          {o[lang]}
        </SelectCard>
      ))}
    </div>
  );
  const MultiGrid = ({ field, options }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {options.map((o) => (
        <SelectCard key={o.v} active={data[field].includes(o.v)} onClick={() => toggle(field, o.v)} testid={`opt-${field}-${o.v}`}>
          {o[lang]}
        </SelectCard>
      ))}
    </div>
  );
  const Dropdown = ({ value, onChange, options, placeholder, testid }) => (
    <div className="relative max-w-md">
      <select
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-[#0A1223] border border-white/12 text-white px-4 py-3.5 pe-10 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-colors appearance-none cursor-pointer"
      >
        <option value="" disabled>{placeholder || "Select..."}</option>
        {options.map((o) => (
          <option key={o.v} value={o.v} style={{ background: "#0A1223", color: "#fff" }}>{o[lang]}</option>
        ))}
      </select>
      <ChevronDown size={18} className="absolute end-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    </div>
  );

  if (success) {
    return (
      <div className="min-h-screen grid place-items-center pt-32 pb-20 px-6" data-testid="rfq-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="glass max-w-xl w-full p-10 text-center"
        >
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/15 grid place-items-center mx-auto mb-6">
            <CheckCircle2 size={44} className="text-[#D4AF37]" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white">{t.rfqPage.successTitle}</h1>
          <p className="mt-4 text-slate-300 leading-relaxed">{t.rfqPage.successMsg}</p>
          <div className="mt-6 bg-[#0A1223] border border-white/12 p-4">
            <p className="section-label !text-slate-400 mb-1">{t.rfqPage.reference}</p>
            <div className="flex items-center justify-center gap-2">
              <span className="font-mono text-[#D4AF37] text-sm">{success.reference}</span>
              <button onClick={() => { navigator.clipboard.writeText(success.reference); toast.success("Copied"); }} className="text-slate-300 hover:text-white">
                <Copy size={15} />
              </button>
            </div>
          </div>
          <div className="mt-6 flex items-start gap-3 bg-[#11203A] border border-[#D4AF37]/20 p-4 text-start">
            <ShieldCheck size={18} className="text-[#D4AF37] shrink-0 mt-0.5" />
            <p className="text-xs text-slate-300 leading-relaxed">{t.rfqPage.warrantyMsg}</p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => { setSuccess(null); setStepIdx(0); setData(INITIAL); }}
              data-testid="rfq-new-request"
              className="btn-gold px-6 py-3"
            >
              {t.rfqPage.newRequest}
            </button>
            <button onClick={() => navigate("/")} className="px-6 py-3 border border-white/20 text-white hover:bg-white/5 transition-colors">
              {t.nav.home}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="rfq-page" className="pt-32 pb-24">
      <div className="relative">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${IMG.heroTower})` }} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center mb-10">
          <p className="section-label mb-3">{t.rfqPage.label}</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-white">{t.rfqPage.heading}</h1>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto">{t.rfqPage.sub}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        {/* Urgent quotation banner */}
        <div className="mb-8">
          <UrgentBanner />
        </div>

        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-[#D4AF37]">
              {t.common.step} {stepIdx + 1} {t.common.of} {TOTAL} — {labelMap[current]}
            </span>
            <span className="text-sm text-slate-400 font-mono">{Math.round(((stepIdx + 1) / TOTAL) * 100)}%</span>
          </div>
          <div className="h-1.5 bg-white/8 overflow-hidden">
            <motion.div className="h-full bg-[#D4AF37]" animate={{ width: `${((stepIdx + 1) / TOTAL) * 100}%` }} transition={{ duration: 0.4 }} />
          </div>
        </div>

        <div className="glass p-7 md:p-10 min-h-[360px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isRTL ? 20 : -20 }}
              transition={{ duration: 0.3 }}
            >
              {current === "need" && (<><StepTitle>{labelMap.need}</StepTitle><SingleGrid field="need" options={rfqOptions.need} /></>)}
              {current === "towerType" && (<><StepTitle>{labelMap.towerType}</StepTitle><Dropdown value={data.tower_type} onChange={(e) => upd("tower_type", e.target.value)} options={rfqOptions.towerType} placeholder={t.rfqPage.selectPlaceholder} testid="rfq-tower-type" /></>)}
              {current === "towerHeight" && (<><StepTitle>{labelMap.towerHeight}</StepTitle><Dropdown value={data.tower_height} onChange={(e) => upd("tower_height", e.target.value)} options={rfqOptions.height} placeholder={t.rfqPage.selectPlaceholder} testid="rfq-tower-height" /></>)}
              {current === "towerQuantity" && (<><StepTitle>{labelMap.towerQuantity}</StepTitle><Dropdown value={data.tower_quantity} onChange={(e) => upd("tower_quantity", e.target.value)} options={rfqOptions.towerQuantity} placeholder={t.rfqPage.selectPlaceholder} testid="rfq-tower-quantity" /></>)}
              {current === "towerConfig" && (
                <div>
                  <StepTitle>{labelMap.towerConfig}</StepTitle>
                  <SingleGrid field="tower_config" options={rfqOptions.towerConfig} cols="grid-cols-1 sm:grid-cols-2" />
                  {data.tower_config === "Multiple Tower Types" && (
                    <motion.label initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="block mt-6">
                      <span className="text-sm font-medium text-slate-300 mb-1.5 block">
                        {t.rfqPage.towerConfigDescLabel}<span className="text-[#D4AF37] ms-1">*</span>
                      </span>
                      <textarea
                        rows={3}
                        value={data.tower_config_description}
                        onChange={(e) => upd("tower_config_description", e.target.value)}
                        placeholder={t.rfqPage.towerConfigDescPlaceholder}
                        data-testid="rfq-towerconfig-desc"
                        className="w-full bg-[#0A1223] border border-white/12 text-white px-4 py-3 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-colors placeholder:text-slate-600"
                      />
                    </motion.label>
                  )}
                </div>
              )}
              {current === "standards" && (
                <>
                  <StepTitle hint={data.need === "Telecom Towers" ? undefined : "Multiple selection"}>{labelMap.standards}</StepTitle>
                  {data.need === "Telecom Towers"
                    ? <Dropdown value={data.standards[0] || ""} onChange={(e) => upd("standards", [e.target.value])} options={rfqOptions.standards} placeholder={t.rfqPage.selectPlaceholder} testid="rfq-standards" />
                    : <MultiGrid field="standards" options={rfqOptions.standards} />}
                </>
              )}
              {current === "scope" && (<><StepTitle>{labelMap.scope}</StepTitle><Dropdown value={data.scope_of_work[0] || ""} onChange={(e) => upd("scope_of_work", [e.target.value])} options={rfqOptions.scope} placeholder={t.rfqPage.selectPlaceholder} testid="rfq-scope" /></>)}

              {/* Telecom Infrastructure */}
              {current === "infraType" && (<><StepTitle hint="Multiple selection">{labelMap.infraType}</StepTitle><MultiGrid field="infra_type" options={rfqOptions.infraType} /></>)}
              {current === "infraScale" && (<><StepTitle>{labelMap.infraScale}</StepTitle><SingleGrid field="infra_scale" options={rfqOptions.infraScale} cols="grid-cols-2 lg:grid-cols-4" /></>)}
              {current === "infraScope" && (<><StepTitle hint="Multiple selection">{labelMap.infraScope}</StepTitle><MultiGrid field="infra_scope" options={rfqOptions.infraScopeOfWork} /></>)}

              {/* Buildings & Civil Projects */}
              {current === "buildingType" && (<><StepTitle>{labelMap.buildingType}</StepTitle><SingleGrid field="building_type" options={rfqOptions.buildingType} /></>)}
              {current === "buildingArea" && (<><StepTitle>{labelMap.buildingArea}</StepTitle><SingleGrid field="building_area" options={rfqOptions.buildingArea} cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" /></>)}
              {current === "buildingFloors" && (<><StepTitle>{labelMap.buildingFloors}</StepTitle><SingleGrid field="building_floors" options={rfqOptions.buildingFloors} cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" /></>)}
              {current === "buildingScope" && (<><StepTitle hint="Multiple selection">{labelMap.buildingScope}</StepTitle><MultiGrid field="building_scope" options={rfqOptions.buildingScope} /></>)}

              {/* Telecom Materials & Equipment */}
              {current === "materialCategory" && (<><StepTitle hint="Multiple selection">{labelMap.materialCategory}</StepTitle><MultiGrid field="material_category" options={rfqOptions.materialCategory} /></>)}
              {current === "materialQuantity" && (<><StepTitle>{labelMap.materialQuantity}</StepTitle><SingleGrid field="material_quantity" options={rfqOptions.materialQuantity} cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" /></>)}
              {current === "materialPurpose" && (<><StepTitle>{labelMap.materialPurpose}</StepTitle><SingleGrid field="material_purpose" options={rfqOptions.materialPurpose} cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" /></>)}

              {/* Engineering Services */}
              {current === "engService" && (<><StepTitle hint="Multiple selection">{labelMap.engService}</StepTitle><MultiGrid field="eng_service" options={rfqOptions.engService} /></>)}
              {current === "engProjectType" && (<><StepTitle>{labelMap.engProjectType}</StepTitle><SingleGrid field="eng_project_type" options={rfqOptions.engProjectType} cols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" /></>)}
              {current === "engDeliverable" && (<><StepTitle hint="Multiple selection">{labelMap.engDeliverable}</StepTitle><MultiGrid field="eng_deliverable" options={rfqOptions.engDeliverable} /></>)}

              {current === "status" && (<><StepTitle>{labelMap.status}</StepTitle><SingleGrid field="project_status" options={rfqOptions.projectStatus} /></>)}
              {current === "delivery" && (<><StepTitle>{labelMap.delivery}</StepTitle><Dropdown value={data.delivery_time} onChange={(e) => upd("delivery_time", e.target.value)} options={rfqOptions.deliveryTime} placeholder={t.rfqPage.selectPlaceholder} testid="rfq-delivery" /></>)}
              {current === "priority" && (<><StepTitle>{labelMap.priority}</StepTitle><Dropdown value={data.priority} onChange={(e) => upd("priority", e.target.value)} options={rfqOptions.priority} placeholder={t.rfqPage.selectPlaceholder} testid="rfq-priority" /></>)}

              {current === "description" && (
                <div>
                  <StepTitle hint={t.rfqPage.descHint}>{descLabel}</StepTitle>
                  <textarea
                    rows={9}
                    value={data.project_description}
                    onChange={(e) => upd("project_description", e.target.value)}
                    placeholder={t.rfqPage.descPlaceholder}
                    data-testid="rfq-description"
                    className="w-full bg-[#0A1223] border border-white/12 text-white px-4 py-3 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-colors placeholder:text-slate-600 resize-y"
                  />
                </div>
              )}

              {current === "documents" && (
                <div>
                  <StepTitle>{labelMap.documents}</StepTitle>
                  <p className="text-sm font-medium text-slate-300 mb-3">{t.rfqPage.docsQuestion}</p>
                  <div className="flex gap-3 mb-6">
                    {["Yes", "No"].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => upd("has_documents", v)}
                        data-testid={`docs-${v.toLowerCase()}`}
                        className={`px-8 py-3 border text-sm font-medium transition-all ${
                          data.has_documents === v ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A1223]" : "bg-[#0A1223] border-white/12 text-slate-200 hover:border-[#D4AF37]/60"
                        }`}
                      >
                        {v === "Yes" ? t.common.yes : t.common.no}
                      </button>
                    ))}
                  </div>

                  {data.has_documents === "Yes" && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                      <p className="text-sm font-medium text-slate-300 mb-3">{t.rfqPage.fileTypes}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mb-6">
                        {rfqOptions.documentTypes.map((o) => (
                          <SelectCard key={o.v} active={data.document_types.includes(o.v)} onClick={() => toggle("document_types", o.v)} testid={`doctype-${o.v}`}>
                            {o[lang]}
                          </SelectCard>
                        ))}
                      </div>
                      <div
                        onClick={() => fileRef.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
                        data-testid="rfq-dropzone"
                        className="border-2 border-dashed border-white/15 hover:border-[#D4AF37]/60 transition-colors p-10 text-center cursor-pointer"
                      >
                        {uploading ? <Loader2 size={32} className="mx-auto text-[#D4AF37] animate-spin" /> : <Upload size={32} className="mx-auto text-[#D4AF37]" />}
                        <p className="mt-4 text-slate-200 font-medium">{uploading ? t.rfqPage.uploading : t.rfqPage.dropFiles}</p>
                        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="rfq-file-input" />
                      </div>
                      {data.files.length > 0 && (
                        <div className="mt-5 space-y-2.5" data-testid="rfq-file-list">
                          {data.files.map((f) => (
                            <div key={f.id} className="flex items-center gap-3 bg-[#0A1223] border border-white/12 px-4 py-3">
                              <FileText size={18} className="text-[#D4AF37] shrink-0" />
                              <span className="text-sm text-slate-200 truncate flex-1">{f.original_filename}</span>
                              <span className="text-xs text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
                              <button onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-400" data-testid={`remove-file-${f.id}`}><X size={16} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              )}

              {current === "location" && (
                <div>
                  <StepTitle>{labelMap.location}</StepTitle>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextInput label={t.rfqPage.country} value={data.country} onChange={(e) => upd("country", e.target.value)} data-testid="rfq-country" required />
                    <TextInput label={t.rfqPage.city} value={data.city} onChange={(e) => upd("city", e.target.value)} data-testid="rfq-city" required />
                    <TextInput label={t.rfqPage.projectName} value={data.project_name} onChange={(e) => upd("project_name", e.target.value)} data-testid="rfq-projectname" required />
                    <TextInput label={t.rfqPage.gps} value={data.gps_coordinates} onChange={(e) => upd("gps_coordinates", e.target.value)} placeholder="24.7136, 46.6753" data-testid="rfq-gps" optional={t.common.optional} />
                  </div>
                  <div className="mt-5">
                    <TextInput label={t.rfqPage.mapsLink} value={data.maps_link} onChange={(e) => upd("maps_link", e.target.value)} placeholder="https://maps.google.com/..." data-testid="rfq-maps" optional={t.common.optional} />
                  </div>
                </div>
              )}

              {current === "customer" && (
                <div>
                  <StepTitle>{labelMap.customer}</StepTitle>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <TextInput label={t.rfqPage.fullName} value={data.full_name} onChange={(e) => upd("full_name", e.target.value)} data-testid="rfq-fullname" required />
                    <TextInput label={t.rfqPage.company} value={data.company_name} onChange={(e) => upd("company_name", e.target.value)} data-testid="rfq-company" required />
                    <TextInput label={t.rfqPage.position} value={data.position} onChange={(e) => upd("position", e.target.value)} data-testid="rfq-position" optional={t.common.optional} />
                    <div>
                      <TextInput label={t.rfqPage.email} type="email" value={data.email} onChange={(e) => upd("email", e.target.value)} data-testid="rfq-email" required />
                      {emailErr && <p className="text-xs text-red-400 mt-1.5" data-testid="rfq-email-error">{t.rfqPage.invalidEmail}</p>}
                    </div>
                    <div>
                      <TextInput label={t.rfqPage.mobile} value={data.mobile} onChange={(e) => upd("mobile", sanitizePhone(e.target.value))} inputMode="tel" placeholder="+966549747549" data-testid="rfq-mobile" required />
                      {mobileErr && <p className="text-xs text-red-400 mt-1.5" data-testid="rfq-mobile-error">{t.rfqPage.invalidPhone}</p>}
                    </div>
                    <div>
                      <TextInput label={t.rfqPage.whatsapp} value={data.whatsapp} onChange={(e) => upd("whatsapp", sanitizePhone(e.target.value))} inputMode="tel" placeholder="+966549747549" data-testid="rfq-whatsapp" required />
                      {whatsappErr && <p className="text-xs text-red-400 mt-1.5" data-testid="rfq-whatsapp-error">{t.rfqPage.invalidPhone}</p>}
                    </div>
                  </div>
                  <label className="block mt-5">
                    <span className="text-sm font-medium text-slate-300 mb-1.5 block">
                      {t.rfqPage.notes} <span className="text-slate-400 text-xs">({t.common.optional})</span>
                    </span>
                    <textarea rows={4} value={data.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="rfq-notes"
                      className="w-full bg-[#0A1223] border border-white/12 text-white px-4 py-3 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-colors" />
                  </label>
                  <div className="mt-6 flex items-start gap-3 bg-[#11203A] border border-[#D4AF37]/20 p-4">
                    <ShieldCheck size={18} className="text-[#D4AF37] shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-300 leading-relaxed" data-testid="rfq-warranty-note">{t.rfqPage.warrantyMsg}</p>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Nav buttons */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={back}
            disabled={stepIdx === 0}
            data-testid="rfq-back"
            className="inline-flex items-center gap-2 px-6 py-3 border border-white/15 text-slate-300 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={18} className={isRTL ? "rotate-180" : ""} /> {t.common.back}
          </button>
          {stepIdx < TOTAL - 1 ? (
            <button onClick={next} data-testid="rfq-next" className="btn-gold inline-flex items-center gap-2 px-7 py-3">
              {t.common.next} <ArrowRight size={18} className={isRTL ? "rotate-180" : ""} />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="rfq-submit" className="btn-gold inline-flex items-center gap-2 px-7 py-3 disabled:opacity-50">
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
              {t.common.submit}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestQuotation;
